import { signInWithCustomToken, signOut } from 'firebase/auth';
import { firebaseAuth } from './firebase';
import { supabase } from './supabase';

/**
 * Web port of Aaptha Mithra's Firebase auth bridge.
 *
 * Calls the existing `firebase-auth` Supabase Edge Function (shared with
 * Aaptha Mithra — same Firebase project, same auth.users table) which mints
 * a Firebase Custom Token whose UID equals the Supabase user UUID. So
 * `req.auth.uid` inside Cloud Functions equals `auth.users.id` in Supabase
 * across BOTH apps — login state is naturally shared.
 *
 * Concurrent calls are deduplicated. Re-syncing is a no-op when Firebase
 * is already signed in with the right UID (the SDK auto-refreshes IDs).
 *
 * Every network step is wrapped in a timeout so a slow Edge Function or a
 * stalled `signInWithCustomToken` can never hang the UI indefinitely (e.g.
 * Submit's "Submitting…" spinner). Failures throw with a readable message
 * so callers can surface a toast instead of silently spinning.
 */

const EDGE_FN_TIMEOUT_MS = 10_000;
const CUSTOM_TOKEN_TIMEOUT_MS = 10_000;

const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);

class FirebaseAuthBridgeService {
  constructor() {
    this._syncPromise = null;
  }

  async syncWithSupabase() {
    if (this._syncPromise) return this._syncPromise;
    this._syncPromise = this._doSync().finally(() => {
      this._syncPromise = null;
    });
    return this._syncPromise;
  }

  async _doSync() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      await this.signOutFirebase();
      return;
    }

    const userId = session.user.id;

    if (firebaseAuth.currentUser && firebaseAuth.currentUser.uid === userId) {
      return;
    }

    let data, error;
    try {
      ({ data, error } = await withTimeout(
        supabase.functions.invoke('firebase-auth'),
        EDGE_FN_TIMEOUT_MS,
        'firebase-auth Edge Function'
      ));
    } catch (e) {
      console.error('[firebaseAuthBridge] Edge Function call failed:', e);
      throw new Error(`Auth bridge unreachable: ${e.message}`);
    }

    if (error || !data?.firebaseToken) {
      const msg = `Failed to mint Firebase token: ${error?.message || 'empty response'}`;
      console.error('[firebaseAuthBridge]', msg);
      throw new Error(msg);
    }

    try {
      await withTimeout(
        signInWithCustomToken(firebaseAuth, data.firebaseToken),
        CUSTOM_TOKEN_TIMEOUT_MS,
        'signInWithCustomToken'
      );
    } catch (e) {
      console.error('[firebaseAuthBridge] signInWithCustomToken failed:', e);
      throw new Error(`Firebase sign-in failed: ${e.message}`);
    }
  }

  async signOutFirebase() {
    try {
      if (firebaseAuth.currentUser) {
        await signOut(firebaseAuth);
      }
    } catch (err) {
      console.error('[firebaseAuthBridge] sign-out failed:', err);
    }
  }

  isFirebaseSynced() {
    return !!firebaseAuth.currentUser;
  }
}

export const firebaseAuthBridge = new FirebaseAuthBridgeService();
export default firebaseAuthBridge;
